import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import BatchFormContext from './BatchFormContext';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Divider from '@material-ui/core/Divider';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import BatchActionForm from './BatchActionForm';
import BatchMappingForm from './BatchMappingForm';
import BatchValidation from './BatchValidation';
import DatasetForm from '../DatasetForm';
import RegisterFormProvider from '../RegisterFormProvider';
import { batches as batchesApi } from '@afosto/content/api/Batches';
import translations from './translations';

const steps = [
  {
    key: 'dataset',
    label: 'Data',
  },
  {
    key: 'action',
    label: 'Action',
  },
  {
    key: 'mapping',
    label: 'Mapping',
  },
  {
    key: 'validation',
    label: 'Validation',
  },
];

const BatchForm = ({
  actionsCode: providedActionsCode,
  action: providedAction,
  excludeActions,
  config: providedConfig,
  dataset: providedDataset,
  onCancel,
  onFinish,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const actionFormRef = useRef(undefined);
  const datasetFormRef = useRef(undefined);
  const mappingFormRef = useRef(undefined);
  const [action, setAction] = useState(providedAction);
  const [actionsCode, setActionsCode] = useState(providedActionsCode);
  const [batch, setBatch] = useState(null);
  const [batchReference, setBatchReference] = useState('');
  const [config, setConfig] = useState(providedConfig ?? {});
  const [dataset, setDataset] = useState(providedDataset);
  const [errors, setErrors] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [activeStep, setActiveStep] = useState(providedDataset ? 1 : 0);
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = () => {
    if (onCancel && typeof onCancel === 'function') {
      onCancel();
    }
  };

  const handleRun = async () => {
    try {
      await batchesApi.confirmBatch(batch?.id);

      enqueueSnackbar(intl.formatMessage(translations.feedback.batchStarted), {
        variant: 'success',
      });

      if (onFinish && typeof onFinish === 'function') {
        await onFinish();
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.batchNotStarted), {
        variant: 'error',
      });
    }
  };

  const handleRunLater = async () => {
    try {
      enqueueSnackbar(intl.formatMessage(translations.feedback.batchSaved), {
        variant: 'success',
      });

      if (onFinish && typeof onFinish === 'function') {
        await onFinish();
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.batchNotSaved), {
        variant: 'error',
      });
    }
  };

  const handleNext = async () => {
    setIsLoading(true);

    try {
      if (activeStep === 0) {
        await datasetFormRef.current?.onSubmit();
      }

      if (activeStep === 1) {
        await actionFormRef.current?.onSubmit();
      }

      if (activeStep === 2) {
        await mappingFormRef.current?.onSubmit();
      }
    } catch (err) {
      // Catch only.
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    setActiveStep(previousStep => previousStep - 1);
  };

  const handleDataset = response => {
    setDataset(response?.data ?? null);
    setActiveStep(previousStep => previousStep + 1);
  };

  const handleAction = actionData => {
    setAction(actionData?.action ?? null);
    setBatchReference(actionData?.batchReference ?? '');
    setConfig(actionData?.config ?? {});
    setActiveStep(previousStep => previousStep + 1);
  };

  const handleBatch = batchData => {
    setBatch(batchData ?? null);
    setActiveStep(previousStep => previousStep + 1);
  };

  return (
    <BatchFormContext.Provider
      value={{
        action,
        actionsCode,
        batch,
        batchReference,
        setBatchReference,
        config,
        dataset,
        errors,
        isValid,
        providedAction,
        providedDataset,
        setAction,
        setActionsCode,
        setBatch,
        setConfig,
        setDataset,
        setErrors,
        setIsValid,
      }}
    >
      <Card>
        <Box pt={2} pb={1}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(({ key, label }) => (
              <Step key={key}>
                <StepLabel>
                  {intl.formatMessage({
                    id: `batchForm.steps.${key}`,
                    defaultMessage: label,
                    description: `The label for the ${key} step in the batch form`,
                  })}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Divider variant="middle" />
        <Box p={2} pt={3}>
          {activeStep === 0 && (
            <RegisterFormProvider value={{ formRef: datasetFormRef }}>
              <DatasetForm
                dataset={dataset}
                onSubmit={handleDataset}
                renderActions={false}
                readOnly={!!providedDataset}
                showExistingDataset
              />
            </RegisterFormProvider>
          )}
          {activeStep === 1 && (
            <RegisterFormProvider value={{ formRef: actionFormRef }}>
              <BatchActionForm
                code={actionsCode}
                action={action}
                batchReference={batchReference}
                config={config}
                excludeActions={excludeActions}
                lockAction={!!providedAction}
                onSubmit={handleAction}
                renderActions={false}
              />
            </RegisterFormProvider>
          )}
          {activeStep === 2 && (
            <RegisterFormProvider value={{ formRef: mappingFormRef }}>
              <BatchMappingForm batch={batch} onSubmit={handleBatch} renderActions={false} />
            </RegisterFormProvider>
          )}
          {activeStep === 3 && <BatchValidation />}
        </Box>
        <Box p={2} display="flex" justifyContent="space-between">
          {activeStep === 0 && (
            <Button variant="outlined" disabled={isLoading} onClick={handleCancel}>
              <FormattedMessage {...translations.actions.cancel} />
            </Button>
          )}
          {activeStep > 0 && (
            <Button variant="outlined" disabled={isLoading} onClick={handlePrevious}>
              <FormattedMessage {...translations.actions.previous} />
            </Button>
          )}
          {activeStep < steps.length - 1 && (
            <Button variant="contained" color="primary" disabled={isLoading} onClick={handleNext}>
              <FormattedMessage {...translations.actions.next} />
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <>
              <Button
                variant="outlined"
                color="primary"
                disabled={isLoading || !isValid}
                onClick={handleRunLater}
                style={{ marginLeft: 'auto', marginRight: 8 }}
              >
                <FormattedMessage {...translations.actions.runLater} />
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading || !isValid}
                onClick={handleRun}
              >
                <FormattedMessage {...translations.actions.run} />
              </Button>
            </>
          )}
        </Box>
      </Card>
    </BatchFormContext.Provider>
  );
};

BatchForm.propTypes = {
  action: PropTypes.object,
  actionsCode: PropTypes.string,
  excludeActions: PropTypes.array,
  config: PropTypes.object,
  dataset: PropTypes.object,
  onCancel: PropTypes.func,
  onFinish: PropTypes.func,
};

BatchForm.defaultProps = {
  action: undefined,
  actionsCode: undefined,
  config: undefined,
  dataset: undefined,
  excludeActions: [],
  onCancel: undefined,
  onFinish: undefined,
};

export default BatchForm;
